/**
 * MeetingBookingListPage — Issue #365.
 *
 * Lists meeting room bookings and supports create (admin_affairs:write) and
 * confirm/reject/cancel/complete actions. Backend enforces time-conflict checks
 * and tenant/org scoping; the page only renders results returned by the API.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAffairsPermissions';
import {
  ADMIN_MEETING_BOOKING_STATUS_LABELS,
  useAdminMeetingBookingMutations,
  useAdminMeetingBookings,
  useAdminMeetingRooms,
  type AdminMeetingBookingDto,
} from '@/hooks/useAdminAffairs';

const STATUS_BADGE: Readonly<Record<AdminMeetingBookingDto['status'], string>> = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

export default function MeetingBookingListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.READ);
  const canWrite = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.WRITE);
  const canApprove = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.APPROVE,
  );

  const { items, total, loading, error, fetchBookings } =
    useAdminMeetingBookings();
  const { items: rooms, fetchRooms } = useAdminMeetingRooms();
  const {
    loading: actionLoading,
    error: actionError,
    createBooking,
    confirmBooking,
    rejectBooking,
    cancelBooking,
    completeBooking,
  } = useAdminMeetingBookingMutations();

  const [statusFilter, setStatusFilter] = useState<
    AdminMeetingBookingDto['status'] | ''
  >('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    roomId: '',
    startAt: '',
    endAt: '',
    purpose: '',
    attendeeCount: '' as string,
  });

  useEffect(() => {
    if (!canRead) return;
    void fetchBookings({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchBookings]);

  useEffect(() => {
    if (!canRead) return;
    void fetchRooms({ status: 'active' });
  }, [canRead, fetchRooms]);

  if (!canRead) return <AccessDenied resource="会议室预订" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">会议室预订</h1>
          <p className="text-sm text-slate-500">
            预订请求由后端检查时间冲突；同一会议室不允许在 pending / confirmed
            状态下出现时间重叠。
          </p>
        </div>
        {canWrite ? (
          <button
            type="button"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="booking-create-entry"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? '取消' : '新增预订 →'}
          </button>
        ) : null}
      </header>

      {creating && canWrite ? (
        <Card className="space-y-3 p-4">
          <label className="block text-sm">
            会议室
            <select
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.roomId}
              onChange={(e) => setForm((f) => ({ ...f, roomId: e.target.value }))}
              data-testid="booking-create-room"
            >
              <option value="">请选择</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.roomCode} {r.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            开始时间
            <input
              type="datetime-local"
              className="mt-1 block w-64 rounded border border-slate-300 px-2 py-1"
              value={form.startAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, startAt: e.target.value }))
              }
              data-testid="booking-create-start"
            />
          </label>
          <label className="block text-sm">
            结束时间
            <input
              type="datetime-local"
              className="mt-1 block w-64 rounded border border-slate-300 px-2 py-1"
              value={form.endAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, endAt: e.target.value }))
              }
              data-testid="booking-create-end"
            />
          </label>
          <label className="block text-sm">
            会议主题
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.purpose}
              onChange={(e) =>
                setForm((f) => ({ ...f, purpose: e.target.value }))
              }
              data-testid="booking-create-purpose"
            />
          </label>
          <label className="block text-sm">
            参会人数
            <input
              type="number"
              min={1}
              className="mt-1 block w-32 rounded border border-slate-300 px-2 py-1"
              value={form.attendeeCount}
              onChange={(e) =>
                setForm((f) => ({ ...f, attendeeCount: e.target.value }))
              }
            />
          </label>
          <button
            type="button"
            disabled={actionLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
            data-testid="booking-create-submit"
            onClick={async () => {
              const created = await createBooking({
                roomId: form.roomId,
                startAt: new Date(form.startAt).toISOString(),
                endAt: new Date(form.endAt).toISOString(),
                purpose: form.purpose,
                attendeeCount: form.attendeeCount
                  ? Number(form.attendeeCount)
                  : undefined,
              });
              if (created) {
                setForm({
                  roomId: '',
                  startAt: '',
                  endAt: '',
                  purpose: '',
                  attendeeCount: '',
                });
                setCreating(false);
                await fetchBookings({ status: statusFilter || undefined });
              }
            }}
          >
            {actionLoading ? '提交中…' : '提交'}
          </button>
          {actionError ? (
            <p
              className="text-sm text-red-600"
              data-testid="booking-create-error"
            >
              {actionError.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                (e.target.value as AdminMeetingBookingDto['status']) || '',
              )
            }
            data-testid="booking-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_MEETING_BOOKING_STATUS_LABELS) as [
                AdminMeetingBookingDto['status'],
                string,
              ][]
            ).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">会议室</th>
              <th className="px-3 py-2">开始</th>
              <th className="px-3 py-2">结束</th>
              <th className="px-3 py-2">主题</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={6}
                  data-testid="booking-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={6}
                  data-testid="booking-empty"
                >
                  暂无预订记录
                </td>
              </tr>
            ) : (
              items.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">
                    {b.roomId.slice(0, 12)}
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDateTime(b.startAt)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDateTime(b.endAt)}</td>
                  <td className="px-3 py-2">{b.purpose}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE[b.status]}`}
                    >
                      {ADMIN_MEETING_BOOKING_STATUS_LABELS[b.status]}
                    </span>
                  </td>
                  <td className="space-x-2 px-3 py-2 text-xs">
                    {b.status === 'pending' && canApprove ? (
                      <>
                        <button
                          type="button"
                          className="text-emerald-600 hover:underline"
                          data-testid={`booking-confirm-${b.id}`}
                          onClick={async () => {
                            await confirmBooking(b.id, b.version);
                            await fetchBookings({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          确认
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          data-testid={`booking-reject-${b.id}`}
                          onClick={async () => {
                            const reason = window.prompt('拒绝原因');
                            if (!reason) return;
                            await rejectBooking(b.id, b.version, reason);
                            await fetchBookings({
                              status: statusFilter || undefined,
                            });
                          }}
                        >
                          拒绝
                        </button>
                      </>
                    ) : null}
                    {b.status === 'confirmed' && canApprove ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        data-testid={`booking-complete-${b.id}`}
                        onClick={async () => {
                          await completeBooking(b.id, b.version);
                          await fetchBookings({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        标记完成
                      </button>
                    ) : null}
                    {(b.status === 'pending' || b.status === 'confirmed') &&
                    canWrite ? (
                      <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        data-testid={`booking-cancel-${b.id}`}
                        onClick={async () => {
                          const reason = window.prompt('取消原因（可选）');
                          await cancelBooking(b.id, b.version, reason ?? undefined);
                          await fetchBookings({
                            status: statusFilter || undefined,
                          });
                        }}
                      >
                        取消
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">合计 {total} 条预订</p>
    </div>
  );
}
